
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Calendar, Clock, DollarSign } from 'lucide-react';

interface VisitBlockV2Props {
  visit: any;
  onUpdate: (updates: any) => void;
  onRemove: () => void;
  onAddTreatment: () => void;
  isMember: boolean;
}

const VisitBlockV2 = ({ 
  visit, 
  onUpdate, 
  onRemove, 
  onAddTreatment, 
  isMember 
}: VisitBlockV2Props) => {
  
  const removeTreatment = (treatmentId: string) => {
    const updatedTreatments = visit.treatments.filter(t => t.id !== treatmentId);
    const totalCost = updatedTreatments.reduce((sum, t) => sum + (t.applied_price * t.quantity), 0);
    const estimatedDuration = updatedTreatments.reduce((sum, t) => sum + (t.duration_minutes || 0), 0);
    
    onUpdate({
      treatments: updatedTreatments,
      totalCost,
      estimatedDuration
    });
  };

  const updateTreatmentQuantity = (treatmentId: string, quantity: number) => {
    const updatedTreatments = visit.treatments.map(t => 
      t.id === treatmentId ? { ...t, quantity: Math.max(1, quantity) } : t
    );
    const totalCost = updatedTreatments.reduce((sum, t) => sum + (t.applied_price * t.quantity), 0);
    
    onUpdate({
      treatments: updatedTreatments,
      totalCost
    });
  };

  return (
    <Card className="border-juv-rose-clay/20 bg-white shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="w-10 h-10 bg-juv-terracotta rounded-full flex items-center justify-center text-white font-bold">
              {visit.visitNumber}
            </div>
            <Input
              value={visit.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="text-lg font-semibold bg-transparent border-none p-0 h-auto focus:ring-0 text-juv-deep-chestnut"
              placeholder={`Visit ${visit.visitNumber}`}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="outline" className="text-juv-terracotta border-juv-terracotta">
              <Clock className="h-3 w-3 mr-1" />
              {visit.estimatedDuration}min
            </Badge>
            <Badge variant="outline" className="text-juv-terracotta border-juv-terracotta">
              <DollarSign className="h-3 w-3 mr-1" />
              ${visit.totalCost}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddTreatment}
              className="text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush/30"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Treatment
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onRemove}
              className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Visit Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`visit-${visit.id}-date`} className="text-juv-deep-chestnut">Scheduled Date</Label>
            <Input
              id={`visit-${visit.id}-date`}
              type="date"
              value={visit.scheduledDate}
              onChange={(e) => onUpdate({ scheduledDate: e.target.value })}
              className="border-juv-rose-clay/20 focus:border-juv-terracotta"
            />
          </div>
          <div>
            <Label className="text-juv-deep-chestnut">Credits Required</Label>
            <div className="text-2xl font-bold text-juv-terracotta mt-1">
              {Math.ceil(visit.totalCost / 50)}
            </div>
          </div>
        </div>

        {/* Treatments List */}
        {visit.treatments.length > 0 && (
          <div className="border border-dashed border-juv-rose-clay/30 rounded-lg p-4 bg-slate-50">
            <h4 className="font-medium text-juv-deep-chestnut mb-3">Treatments:</h4>
            <div className="space-y-2">
              {visit.treatments.map((treatment) => (
                <div key={treatment.id} className="flex items-center justify-between p-3 bg-white rounded border border-juv-rose-clay/10">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-juv-deep-chestnut">{treatment.name}</span>
                      {isMember && treatment.member_price < treatment.retail_price && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          Member Price
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-juv-deep-chestnut/60 mt-1">
                      {treatment.duration_minutes}min • {treatment.category}
                      {isMember && (
                        <span className="text-green-600 ml-2">
                          Save ${(treatment.retail_price - treatment.member_price).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      min="1"
                      value={treatment.quantity}
                      onChange={(e) => updateTreatmentQuantity(treatment.id, parseInt(e.target.value))}
                      className="w-16 h-8 text-center border-juv-rose-clay/20"
                    />
                    <span className="text-juv-terracotta font-medium min-w-[60px] text-right">
                      ${(treatment.applied_price * treatment.quantity).toFixed(2)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTreatment(treatment.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {visit.treatments.length === 0 && (
          <div className="border border-dashed border-juv-rose-clay/30 rounded-lg p-8 text-center">
            <p className="text-juv-deep-chestnut/60">No treatments added yet</p>
            <Button
              onClick={onAddTreatment}
              variant="ghost"
              className="mt-2 text-juv-terracotta hover:bg-juv-soft-blush/30"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Treatment
            </Button>
          </div>
        )}

        {/* Visit Notes */}
        <div>
          <Label htmlFor={`visit-${visit.id}-notes`} className="text-juv-deep-chestnut">Visit Notes</Label>
          <Textarea
            id={`visit-${visit.id}-notes`}
            value={visit.notes}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Add notes for this visit..."
            className="mt-1 border-juv-rose-clay/20 focus:border-juv-terracotta"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default VisitBlockV2;
